import { Injectable, inject } from '@angular/core';
import { Observable, interval } from 'rxjs';
import { map, catchError, switchMap, startWith } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import { CatalogResolverService } from './catalog-resolver.service';
import { APP_CONFIG } from '@cfg/env/app.config';
import { UpdatePrioritizationRequest } from '@domain/service/traceability/traceability-command.port';
import {
  CaseListItemDTO,
  AuditLogDTO,
  CasesQueryParams,
  PaginatedCasesResponse,
  VersionHistoryEntry,
  VersionChange,
  TraceabilityMetricsResponse
} from '../api/traceability-api.interface';
import {
  TraceabilityMetrics,
  TraceabilityMetricsFilterParams
} from '@model/traceability/traceability.model';

export interface SamplesPaginatedResult {
  samples: CaseListItemDTO[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TraceabilityService {
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly catalogResolver = inject(CatalogResolverService);

  private readonly SAMPLES_URL = 'samples/traceability';
  private readonly METRICS_URL = 'samples/traceability/metrics';
  private readonly AUDIT_URL = 'audit-logs';

  private readonly LBL_CODIGO_LG = 'Código LG';
  private readonly LBL_ACCION = 'Acción';
  private readonly LBL_CODIGO_MUESTRA = 'Código Muestra';
  private readonly LBL_EN_SECUENCIACION = 'En Secuenciación';
  private readonly LBL_OBSERVACION_REGISTRADA = 'Observación Registrada';
  private readonly LBL_ETIQUETA_IMPRESA = 'Etiqueta Impresa';
  private readonly LBL_ASIGNADO_WORKLIST = 'Asignado a Lista de Trabajo';
  private readonly LBL_CODIGO_CORRIDA = 'Código Corrida';
  private readonly LBL_REGISTRO_INICIAL = 'Registro inicial';
  private readonly LBL_TECNOLOGIA = 'Tecnología';
  private readonly LBL_ESTADO = 'Estado';
  private readonly LBL_OBSERVACION = 'Observación';
  private readonly LBL_UBICACION_POCILLO = 'Ubicación Pocillo';
  private readonly LBL_CAUSA = 'Causa';

  getSamples(params?: CasesQueryParams): Observable<SamplesPaginatedResult> {
    const queryParams = this.buildQueryParams(params);

    return this.httpClient
      .getRaw<PaginatedCasesResponse>(`${this.SAMPLES_URL}${queryParams}`)
      .pipe(
        map((response) => ({
          samples: response.content.map((c) => ({
            ...c,
            patientName:
              `${c.patientFirstName || ''} ${c.patientLastName || ''}`.trim(),
            age: c.age ?? this.calculateAge(c.birthDate),
            outputDate:
              c.outputDate ?? this.calculateOutputDate(c.sampleArrivalDate)
          })),
          pagination: {
            page: response.page,
            size: response.size,
            total: response.totalElements,
            totalPages: response.totalPages
          }
        })),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getSamples', error)
        )
      );
  }

  private calculateAge(birthDate: string | null): number | null {
    if (!birthDate) {
      return null;
    }

    try {
      const birth = new Date(birthDate);
      const today = new Date();

      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }

      return age >= 0 ? age : null;
    } catch {
      return null;
    }
  }

  private calculateOutputDate(sampleArrivalDate: string | null): string | null {
    if (!sampleArrivalDate) {
      return null;
    }

    try {
      const startDate = new Date(sampleArrivalDate);
      let businessDaysAdded = 0;
      const targetBusinessDays = 30;

      const resultDate = new Date(startDate);

      while (businessDaysAdded < targetBusinessDays) {
        resultDate.setDate(resultDate.getDate() + 1);
        const dayOfWeek = resultDate.getDay();

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDaysAdded++;
        }
      }

      return resultDate.toISOString();
    } catch {
      return null;
    }
  }


  exportSamplesToCSV(params?: CasesQueryParams): Observable<Blob> {
    const queryParams = this.buildQueryParams(params);
    const exportUrl = `${this.SAMPLES_URL}/export${queryParams}`;

    return this.httpClient.getBlob(exportUrl).pipe(
      catchError((error: HttpErrorResponse) =>
        this.errorHandler.handleError('exportSamplesToCSV', error)
      )
    );
  }


  getCaseHistory(identifier: string): Observable<AuditLogDTO[]> {
    return this.catalogResolver
      .loadCatalogs()
      .pipe(
        switchMap(() =>
          this.httpClient
            .getRaw<AuditLogDTO[]>(`${this.AUDIT_URL}/${identifier}`)
            .pipe(
              catchError((error: HttpErrorResponse) =>
                this.errorHandler.handleError('getCaseHistory', error)
              )
            )
        )
      );
  }


  getMetrics(params?: TraceabilityMetricsFilterParams): Observable<TraceabilityMetrics> {
    const queryParams = this.buildMetricsQueryParams(params);

    const laboratoryContext = this.appConfig.laboratory?.context || '/api/laboratorio';
    const baseUrl = this.appConfig.backend.baseUrl || '';
    const fullUrl = `${baseUrl}${laboratoryContext}/${this.METRICS_URL}${queryParams}`;

    return this.http
      .get<TraceabilityMetricsResponse>(fullUrl)
      .pipe(
        map((response) => this.mapMetricsResponse(response)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getMetrics', error)
        )
      );
  }


  getMetricsStream(
    params?: TraceabilityMetricsFilterParams,
    intervalMs = 60000
  ): Observable<TraceabilityMetrics> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getMetrics(params))
    );
  }

  private buildMetricsQueryParams(params?: TraceabilityMetricsFilterParams): string {
    if (!params) {
      return '';
    }

    const queryParts: string[] = [];

    if (params.statusIds?.length) {
      queryParts.push(`statusIds=${params.statusIds.join(',')}`);
    }
    if (params.facilityIds?.length) {
      queryParts.push(`facilityIds=${params.facilityIds.join(',')}`);
    }
    if (params.priorityIds?.length) {
      queryParts.push(`priorityIds=${params.priorityIds.join(',')}`);
    }
    if (params.startDate) {
      queryParts.push(`startDate=${encodeURIComponent(params.startDate)}`);
    }
    if (params.endDate) {
      queryParts.push(`endDate=${encodeURIComponent(params.endDate)}`);
    }

    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  private mapMetricsResponse(response: TraceabilityMetricsResponse): TraceabilityMetrics {
    return {
      totalGeneral: response.totalGeneral ?? 0,
      totalDelDia: response.totalDelDia ?? 0,
      pendienteExtraccion: response.pendienteExtraccion ?? 0,
      enProceso: response.enProceso ?? 0,
      extraccionCompletada: response.extraccionCompletada ?? 0,
      enLibreria: response.enLibreria ?? 0,
      enCorrida: response.enCorrida ?? 0,
      ngs: response.ngs ?? 0,
      microarreglos: response.microarreglos ?? 0,
      mlpa: response.mlpa ?? 0,
      expansionTripletas: response.expansionTripletas ?? 0
    };
  }


  transformToVersionHistory(auditLogs: AuditLogDTO[]): VersionHistoryEntry[] {
    const versions: VersionHistoryEntry[] = [];

    // Group by unique state changes and create versions
    let versionNumber = auditLogs.length;

    for (const log of auditLogs) {
      const changes = this.parseAuditLogToChanges(log);

      versions.push({
        version: `${versionNumber}.0`,
        date: log.creationDate,
        user: log.auditUser,
        eventType: log.eventType || 'TRANSACTION',
        status: this.formatStatusValue(log.newValue) ?? undefined,
        changes
      });

      versionNumber--;
    }

    return versions.reverse(); // Most recent first
  }

  private parseAuditLogToChanges(log: AuditLogDTO): VersionChange[] {
    const changes: VersionChange[] = [];

    if (log.newValue) {
      changes.push({
        field: this.LBL_ESTADO,
        oldValue: this.formatStatusValue(log.oldValue),
        newValue: this.formatStatusValue(log.newValue)
      });
    }

    if (
      log.fieldName &&
      log.fieldName !== 'order' &&
      log.fieldName !== 'specimen' &&
      log.fieldName !== 'status'
    ) {
      changes.push({
        field: this.formatFieldLabel(log.fieldName),
        oldValue: this.formatStatusValue(log.oldValue),
        newValue: this.formatStatusValue(log.newValue)
      });
    }

    if (
      log.eventType &&
      log.eventType !== 'TRANSACTION' &&
      log.eventType !== 'CREATED' &&
      log.eventType !== 'STATUS_CHANGED'
    ) {
      changes.push({
        field: 'Tipo de Evento',
        oldValue: null,
        newValue: this.formatEventType(log.eventType)
      });
    }

    if (log.message) {
      const messageChanges = this.parseMessageToChanges(log.message);
      changes.push(...messageChanges);
    }

    if (!changes.some((change) => change.field === this.LBL_ACCION)) {
      const isUpdateEvent = log.eventType === 'UPDATED';
      const isTransactionEvent = log.eventType === 'TRANSACTION';

      if (isUpdateEvent || isTransactionEvent) {
        changes.unshift({
          field: this.LBL_ACCION,
          oldValue: null,
          newValue: this.formatActionLabel('EDIT')
        });
      }
    }

    if (changes.length === 0) {
      return [
        { field: this.LBL_ACCION, oldValue: null, newValue: this.LBL_REGISTRO_INICIAL }
      ];
    }

    return changes;
  }

  private formatFieldLabel(field: string): string {
    const fieldLabels: Record<string, string> = {
      status: this.LBL_ESTADO,
      priority: 'Prioridad',
      observations: 'Observaciones',
      diagnosis: 'Diagnóstico',
      exam_code: 'Código Examen',
      lg_number: this.LBL_CODIGO_LG,
      specimen_code: this.LBL_CODIGO_MUESTRA
    };
    return fieldLabels[field.toLowerCase()] || this.formatFieldName(field);
  }

  private formatStatusValue(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const statusLabels: Record<string, string> = {
      RECEIVED: 'Recibido',
      IN_PROCESS: 'En Proceso',
      PENDING_EXTRACTION: 'Pendiente de Extracción',
      IN_EXTRACTION: 'En Extracción',
      EXTRACTED: 'Extraído',
      EXTRACTION_COMPLETED: 'Extracción Completada',
      IN_LIBRARY: 'En Librería',
      IN_SEQUENCING: this.LBL_EN_SECUENCIACION,
      COMPLETED: 'Completado',
      PENDING_ASSIGNMENT: 'Pendiente asignación',
      PENDING_ANALYSIS: 'Pendiente análisis',
      IN_ANALYSIS: 'En análisis',
      REPORT_IN_PROGRESS: 'Informe en elaboración',
      IN_SIGNATURE: 'En firma',
      ANALYST_SIGNED: 'Firmado analista',
      VALIDATOR_SIGNED: 'Firmado validador',
      READY_TO_SEND: 'Listo para envío',
      SENT: 'Enviado',
      PENDING_ADDENDUM: 'Pendiente adendum',
      ADMITTED: 'Admitido',
      CANCELLED: 'Cancelado',
      REPROCESS: 'Reproceso',
      ANNULLED: 'Anulado',
      REGISTERED: this.LBL_OBSERVACION_REGISTRADA,
      LABEL_PRINTED: this.LBL_ETIQUETA_IMPRESA,
      ASSIGNED_TO_WORKLIST: this.LBL_ASIGNADO_WORKLIST,
      IN_RUN: 'En Corrida',
      EN_CORRIDA: 'En Corrida',
      EN_LIBRERÍA: 'En Librería',
      RUNNING: this.LBL_EN_SECUENCIACION,
      CREATED: 'Creada',
      COMPLETED_SUCCESS: 'Completada Exitosamente',
      COMPLETED_ERRORS: 'Completada con Errores',
      'En Secuenciación': this.LBL_EN_SECUENCIACION,
      Creada: 'Creada',
      'Completada Exitosamente': 'Completada Exitosamente',
      'Completada con Errores': 'Completada con Errores'
    };
    return statusLabels[value] || value;
  }

  private formatEventType(eventType: string): string {
    const eventLabels: Record<string, string> = {
      STATUS_CHANGED: 'Cambio de Estado',
      CREATED: 'Creación',
      UPDATED: 'Actualización',
      DELETED: 'Eliminación',
      SENT: 'Envío',
      REPROCESS_MARKED: 'Marcado para Reproceso',
      VALIDATED: 'Validación'
    };
    return eventLabels[eventType] || eventType;
  }

  private parseMessageToChanges(message: string): VersionChange[] {
    if (!message) {
      return [
        { field: this.LBL_ACCION, oldValue: null, newValue: this.LBL_REGISTRO_INICIAL }
      ];
    }

    const translatedMessage = this.translateKnownMessages(message);

    return this.parseByPrefix(translatedMessage)
        ?? this.parseByRegex(translatedMessage)
        ?? this.parseAsJsonOrPlainText(translatedMessage);
  }

  private parseByPrefix(msg: string): VersionChange[] | null {
    if (msg.includes('GUARDADO DEFINITIVO') || msg.includes('Guardado definitivo')) {
      return this.parseDetailedMessage(msg);
    }
    if (msg.startsWith('ETIQUETA IMPRESA - LG:')) {
      return this.parseLabelPrintedMessage(msg);
    }
    if (
      msg.startsWith('EXTRACCIÓN MANUAL - LG:') ||
      msg.startsWith('EXTRACCIÓN ASOCIADA - LG:') ||
      msg.toLowerCase().startsWith('asociación de extracción exitosa')
    ) {
      return this.parseManualExtractionMessage(msg);
    }
    if (msg.startsWith('EXTRACCIÓN APROBADA - LG:')) {
      return this.parseExtractionApprovedMessage(msg);
    }
    if (msg.startsWith('CORRIDA EDITADA EXITOSAMENTE') || msg.startsWith('SEQUENCING_RUN_EDITED')) {
      return this.parseSequencingRunEditedMessage(msg);
    }
    if (msg.startsWith('REPROCESO EN CORRIDA EXITOSA')) {
      return this.parseReprocessInSuccessfulRunMessage(msg);
    }
    if (msg.startsWith('REPROCESO EN CORRIDA NO EXITOSA')) {
      return this.parseReprocessInFailedRunMessage(msg);
    }
    return null;
  }

  private parseByRegex(msg: string): VersionChange[] | null {
    const familyMemberRegex = /^Creación de Familiar\s*-\s*([a-záéíóúñ\s]+)\s*-\s*LG:\s*([A-Z0-9]+)\s*-\s*(\{.+\})$/is;
    const familyMemberMatch = familyMemberRegex.exec(msg);
    if (familyMemberMatch) {
      return this.parseFamilyMemberMessage(familyMemberMatch);
    }

    const actionLgJsonRegex = /^([áéíóúñü\w\s]+)\s*-\s*LG:\s*([A-Z0-9]+)\s*-\s*(\{.+\})$/is;
    const actionLgJsonMatch = actionLgJsonRegex.exec(msg);
    if (actionLgJsonMatch) {
      return this.parseActionLgJsonMessage(actionLgJsonMatch);
    }

    const actionJsonRegex = /^([áéíóúñüÁÉÍÓÚÑ\w\s]+)\s*-\s*(\{.+\})$/s;
    const actionJsonMatch = actionJsonRegex.exec(msg);
    if (actionJsonMatch) {
      return this.parseActionWithJson(actionJsonMatch[1].trim(), actionJsonMatch[2]);
    }

    const actionLgRegex = /^([áéíóúñü\w\s]+)\s*-\s*LG:\s*(.+)$/i;
    const actionLgMatch = actionLgRegex.exec(msg);
    if (actionLgMatch) {
      return [
        { field: this.LBL_ACCION, oldValue: null, newValue: this.formatActionLabel(actionLgMatch[1].trim()) },
        { field: this.LBL_CODIGO_LG, oldValue: null, newValue: actionLgMatch[2].trim() }
      ];
    }

    return null;
  }

  private parseFamilyMemberMessage(match: RegExpExecArray): VersionChange[] {
    const role = match[1].trim();
    const lgNumber = match[2].trim();
    const jsonPart = match[3];
    const changes: VersionChange[] = [
      { field: this.LBL_ACCION, oldValue: null, newValue: 'Ingreso por Código / Id - Registro completado' },
      { field: 'Rol Familiar', oldValue: null, newValue: role },
      { field: this.LBL_CODIGO_LG, oldValue: null, newValue: lgNumber }
    ];
    try {
      const parsed = JSON.parse(jsonPart) as Record<string, unknown>;
      changes.push(...this.parseJsonToChanges(parsed));
    } catch {
      changes.push({ field: 'Detalle', oldValue: null, newValue: jsonPart });
    }
    return changes;
  }

  private parseActionLgJsonMessage(match: RegExpExecArray): VersionChange[] {
    const action = match[1].trim();
    const lgNumber = match[2].trim();
    const jsonPart = match[3];
    const changes: VersionChange[] = [
      { field: this.LBL_ACCION, oldValue: null, newValue: this.formatActionLabel(action) },
      { field: this.LBL_CODIGO_LG, oldValue: null, newValue: lgNumber }
    ];
    try {
      const parsed = JSON.parse(jsonPart) as Record<string, unknown>;
      const jsonChanges = this.parseJsonToChanges(parsed);
      changes.push(...jsonChanges.filter((c) => c.field !== this.LBL_ACCION));
    } catch {
      // If JSON parsing fails, just continue with what we have
    }
    return changes;
  }

  private parseAsJsonOrPlainText(msg: string): VersionChange[] {
    try {
      const parsed = JSON.parse(msg) as Record<string, unknown>;
      return this.parseJsonToChanges(parsed);
    } catch {
      return [{ field: 'Detalle', oldValue: null, newValue: msg }];
    }
  }

  private translateKnownMessages(message: string): string {
    if (message.startsWith('ANNULMENT - LG:')) {
      return message.replace('ANNULMENT - LG:', 'ANULACIÓN - LG:');
    }
    if (message.startsWith('ORDER_ANNULLED - LG:')) {
      return message.replace('ORDER_ANNULLED - LG:', 'ORDEN ANULADA - LG:');
    }
    if (message.startsWith('LABEL_PRINTED - LG:')) {
      return message.replace('LABEL_PRINTED - LG:', 'ETIQUETA IMPRESA - LG:');
    }
    if (message.startsWith('EXTRACTION_ASSOCIATED - LG:')) {
      return message.replace(
        'EXTRACTION_ASSOCIATED - LG:',
        'EXTRACCIÓN ASOCIADA - LG:'
      );
    }
    if (message.startsWith('MANUAL_EXTRACTION - LG:')) {
      return message.replace(
        'MANUAL_EXTRACTION - LG:',
        'EXTRACCIÓN MANUAL - LG:'
      );
    }
    if (message.startsWith('EXTRACTION_EDITED - LG:')) {
      return message.replace(
        'EXTRACTION_EDITED - LG:',
        'Extracción editada - LG:'
      );
    }
    if (message.startsWith('REPROCESS_REPORTED - LG:')) {
      return message.replace(
        'REPROCESS_REPORTED - LG:',
        'REPROCESO REPORTADO - LG:'
      );
    }
    if (message.startsWith('EXTRACTION_APPROVED - LG:')) {
      return message.replace(
        'EXTRACTION_APPROVED - LG:',
        'EXTRACCIÓN APROBADA - LG:'
      );
    }
    if (message.startsWith('SEQUENCING_RUN_EDITED')) {
      return message.replace(
        'SEQUENCING_RUN_EDITED - Corrida editada exitosamente',
        'CORRIDA EDITADA EXITOSAMENTE'
      );
    }
    if (message.startsWith('REPROCESO EN CORRIDA EXITOSA')) {
      return message; // Already in Spanish, no translation needed
    }
    if (message.startsWith('REPROCESO EN CORRIDA NO EXITOSA')) {
      return message; // Already in Spanish, no translation needed
    }
    return message;
  }

  private parseActionWithJson(
    action: string,
    jsonString: string
  ): VersionChange[] {
    const changes: VersionChange[] = [];

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: this.formatActionLabel(action)
    });

    try {
      const parsed = JSON.parse(jsonString) as Record<string, unknown>;
      const jsonChanges = this.parseJsonToChanges(parsed);
      changes.push(...jsonChanges);
    } catch {
      changes.push({
        field: 'Datos',
        oldValue: null,
        newValue: jsonString
      });
    }

    return changes;
  }

  private parseJsonToChanges(parsed: Record<string, unknown>): VersionChange[] {
    const changes: VersionChange[] = [];

    const fieldLabels: Record<string, string> = {
      idNumber: 'Documento',
      identifierTypeCode: 'Tipo Documento',
      patientNumber: 'Número Paciente',
      givenName: 'Nombre',
      middleName: 'Segundo Nombre',
      familyName: 'Apellido',
      maternalFamilyName: 'Apellido Materno',
      birthDate: 'Fecha Nacimiento',
      administrativeSex: 'Sexo',
      maritalStatus: 'Estado Civil',
      race: 'Raza',
      telephoneNumber: 'Teléfono',
      emailAddress: 'Email',
      streetAddress: 'Dirección',
      city: 'Ciudad',
      state: 'Departamento',
      country: 'País',
      specimenId: this.LBL_CODIGO_MUESTRA,
      specimenTypeId: 'Tipo Muestra',
      specimenContainerId: 'Contenedor',
      specimenPriorityId: 'Prioridad Muestra',
      descriptionDataSample: 'Descripción Muestra',
      placerOrderNumber: 'Número Orden',
      caseNumber: 'Número Caso',
      examId: 'Examen',
      descriptionDataExam: 'Descripción Examen',
      clinicalHistory: 'Historia Clínica',
      userDataDescription: 'Datos Adicionales',
      companyId: 'Compañía',
      providerId: 'Proveedor',
      regionalId: 'Regional',
      siteId: 'Sede',
      laboratoryId: 'Laboratorio',
      specialistId: 'Especialista',
      diagnosisId: 'Diagnóstico',
      acmg: 'ACMG',
      mlpa: 'MLPA',
      prioritization: 'Priorizado',
      priority: 'Prioridad',
      consentForUseOfSamples: 'Consentimiento Investigación',
      reprocess: 'Reproceso',
      causeId: 'Causa Reproceso',
      statusId: 'Estado Reproceso',
      reprocessStatusId: 'Estado Reproceso',
      reprocessCauseId: 'Causa Reproceso',
      reprocessObservation: 'Observación Reproceso',
      observations: this.LBL_OBSERVACION,
      context: 'Contexto',
      observationText: this.LBL_OBSERVACION,
      well_location: this.LBL_UBICACION_POCILLO,
      wellLocation: this.LBL_UBICACION_POCILLO,
      concentration: 'Concentración',
      ratio260_230: 'Relación 260/230',
      ratio260_280: 'Relación 260/280'
    };

    const excludeFields = ['admissionDatetime'];

    for (const [key, value] of Object.entries(parsed)) {
      if (excludeFields.includes(key)) continue;
      if (value === null || value === undefined || value === '') continue;

      const label = fieldLabels[key] || this.formatFieldName(key);
      const formattedValue = this.formatJsonValue(key, value);

      changes.push({
        field: label,
        oldValue: null,
        newValue: formattedValue
      });
    }

    return changes;
  }

  private formatJsonValue(key: string, value: unknown): string {
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }

    const valueStr = String(value);

    // Format date fields (birthDate, etc.) - convert "2026,2,3" to "2026-02-03"
    const dateFields = ['birthdate', 'fechanacimiento', 'birthDate'];
    if (dateFields.includes(key.toLowerCase()) && valueStr.match(/^\d{4},\d{1,2},\d{1,2}$/)) {
      const parts = valueStr.split(',');
      const year = parts[0];
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      if (this.catalogResolver.shouldResolve(key)) {
        const resolved = this.catalogResolver.resolveValue(key, valueStr);
        if (resolved !== valueStr) {
          return resolved;
        }
      }

      const isUUID =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          valueStr
        );
      if (isUUID) {
        return valueStr.substring(0, 8) + '...';
      }
    }

    return valueStr;
  }

  private formatActionLabel(action: string): string {
    const actionLabels: Record<string, string> = {
      MANUAL_ENTRY: 'Ingreso Manual - Muestra registrada',
      ENTRY_BY_CODE: 'Ingreso por Código / Id - Registro completado',
      SAMPLE_EDITED: 'Edición de Muestra - Registro actualizado',
      REPROCESS_ADDED: 'Reproceso Agregado - Muestra marcada para reproceso',
      REPROCESS_UPDATED: 'Reproceso Editado - Estado actualizado',
      REPROCESS_REPORTED: 'Reproceso Reportado',
      ORDER_ANNULLED: 'Orden Anulada',
      OBSERVATION_REGISTERED: this.LBL_OBSERVACION_REGISTRADA,
      FINAL_SAVE: 'Guardado Definitivo - Enviado a Laboratorio',
      EDIT: 'Edición - Muestra actualizada',
      LABEL_PRINTED: this.LBL_ETIQUETA_IMPRESA,
      LIBRARY_CREATED: 'Librería Creada',
      'EXTRACCIÓN APROBADA': 'Extracción Aprobada',
      'INGRESO MANUAL': 'Ingreso Manual - Muestra registrada',
      'INGRESO POR CÓDIGO': 'Ingreso por Código / Id - Registro completado',
      'EDICIÓN DE MUESTRA': 'Edición de Muestra - Registro actualizado',
      'REPROCESO AGREGADO':
        'Reproceso Agregado - Muestra marcada para reproceso',
      'REPROCESO EDITADO': 'Reproceso Editado - Estado actualizado',
      'REPROCESO REPORTADO': 'Reproceso Reportado',
      'ORDEN ANULADA': 'Orden Anulada',
      'OBSERVACIÓN REGISTRADA': this.LBL_OBSERVACION_REGISTRADA,
      'GUARDADO DEFINITIVO': 'Guardado Definitivo - Enviado a Laboratorio',
      EDICIÓN: 'Edición - Muestra actualizada',
      'ETIQUETA IMPRESA': this.LBL_ETIQUETA_IMPRESA,
      'LIBRERÍA CREADA': 'Librería Creada',
      'EXTRACCIÓN EDITADA': 'Extracción editada',
      'Extracción editada': 'Extracción editada',
      'ASIGNADO A LISTA DE TRABAJO': this.LBL_ASIGNADO_WORKLIST,
      ASSIGNED_TO_WORKLIST: this.LBL_ASIGNADO_WORKLIST,
      'LIBRERÍA MLPA CREADA': 'Grupo de Montaje MLPA Creado',
      'LIBRERÍA MICROARREGLOS CREADA': 'Librería Microarreglos Creada',
      'CREACIÓN DE CHIP EN MICROARREGLOS': 'Creación de Chip en Microarreglos',
      'LIBRERÍA EXPANSIÓN TRIPLETAS CREADA':
        'Grupo de Montaje Expansión Tripletas Creado',
      'LIBRERÍA NGS CREADA': 'Librería NGS Creada',
      'CORRIDA MLPA CREADA': 'Plato MLPA Creado',
      'CORRIDA MICROARREGLOS CREADA': 'Corrida Microarreglos Creada',
      'CORRIDA EXPANSIÓN TRIPLETAS CREADA':
        'Plato Expansión Tripletas Creado',
      'CORRIDA NGS CREADA': 'Corrida NGS Creada',
      'CORRIDA MLPA EN SECUENCIACIÓN': 'Plato MLPA En Secuenciación',
      'CORRIDA NGS EN SECUENCIACIÓN': 'Corrida NGS En Secuenciación',
      'CORRIDA MICROARREGLOS EN SECUENCIACIÓN': 'Corrida Microarreglos En Secuenciación',
      'CORRIDA EXPANSIÓN TRIPLETAS EN SECUENCIACIÓN': 'Plato Expansión Tripletas En Secuenciación',
      'CORRIDA MLPA COMPLETADA EXITOSAMENTE': 'Plato MLPA Completado Exitosamente',
      'CORRIDA NGS COMPLETADA EXITOSAMENTE': 'Corrida NGS Completada Exitosamente',
      'CORRIDA MICROARREGLOS COMPLETADA EXITOSAMENTE': 'Corrida Microarreglos Completada Exitosamente',
      'CORRIDA EXPANSIÓN TRIPLETAS COMPLETADA EXITOSAMENTE': 'Plato Expansión Tripletas Completado Exitosamente',
      'CORRIDA MLPA COMPLETADA CON ERRORES': 'Plato MLPA Completado con Errores',
      'CORRIDA NGS COMPLETADA CON ERRORES': 'Corrida NGS Completada con Errores',
      'CORRIDA MICROARREGLOS COMPLETADA CON ERRORES': 'Corrida Microarreglos Completada con Errores',
      'CORRIDA EXPANSIÓN TRIPLETAS COMPLETADA CON ERRORES': 'Plato Expansión Tripletas Completado con Errores',
      'REPROCESO EN CORRIDA EXITOSA MLPA': 'Reproceso en Plato Exitoso MLPA',
      'REPROCESO EN CORRIDA EXITOSA NGS': 'Reproceso en Corrida Exitosa NGS',
      'REPROCESO EN CORRIDA EXITOSA MICROARREGLOS': 'Reproceso en Corrida Exitosa Microarreglos',
      'REPROCESO EN CORRIDA EXITOSA EXPANSIÓN TRIPLETAS': 'Reproceso en Plato Exitoso Expansión Tripletas',
      'REPROCESO EN CORRIDA NO EXITOSA MLPA': 'Reproceso en Plato No Exitoso MLPA',
      'REPROCESO EN CORRIDA NO EXITOSA NGS': 'Reproceso en Corrida No Exitosa NGS',
      'REPROCESO EN CORRIDA NO EXITOSA MICROARREGLOS': 'Reproceso en Corrida No Exitosa Microarreglos',
      'REPROCESO EN CORRIDA NO EXITOSA EXPANSIÓN TRIPLETAS': 'Reproceso en Plato No Exitoso Expansión Tripletas'
    };
    return actionLabels[action] || action;
  }

  private parseLabelPrintedMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];
    const lgCode = message.replace('ETIQUETA IMPRESA - LG:', '').trim();

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: this.formatActionLabel('ETIQUETA IMPRESA')
    });

    if (lgCode) {
      changes.push({
        field: this.LBL_CODIGO_LG,
        oldValue: null,
        newValue: lgCode
      });
    }

    return changes;
  }

  private parseManualExtractionMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: 'Asociación de extracción exitosa'
    });

    // Check if message has pipe-separated fields (new format)
    if (message.includes('|')) {
      this.parsePipeSeparatedFields(message, changes);
    } else {
      // Legacy format: just extract LG code
      this.extractLgCode(message, changes);
    }

    return changes;
  }

  private parsePipeSeparatedFields(message: string, changes: VersionChange[]): void {
    const parts = message.split('|').map((part) => part.trim());

    const fieldMap: Record<string, string> = {
      LG: this.LBL_CODIGO_LG,
      Pocillo: this.LBL_UBICACION_POCILLO,
      Concentración: 'Concentración',
      'Relación 260/280': 'Relación 260/280',
      'Relación 260/230': 'Relación 260/230'
    };

    for (const part of parts) {
      const colonIndex = part.indexOf(':');
      if (colonIndex <= 0) {
        continue;
      }
      const key = part.substring(0, colonIndex).trim();
      const value = part.substring(colonIndex + 1).trim();
      const fieldName = fieldMap[key] || key;

      if (value && value !== '-' && value !== '0') {
        changes.push({
          field: fieldName,
          oldValue: null,
          newValue: value
        });
      }
    }
  }

  private extractLgCode(message: string, changes: VersionChange[]): void {
    const lgMatch = /LG[:\s]+([A-Z0-9-]+)/i.exec(message);
    const lgCode = lgMatch?.[1]?.trim();

    if (lgCode) {
      changes.push({
        field: this.LBL_CODIGO_LG,
        oldValue: null,
        newValue: lgCode
      });
    }
  }

  private parseExtractionApprovedMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    // Extract LG code from message
    const lgMatch = /LG[:\s]+([A-Z0-9-]+)/i.exec(message);
    const lgCode = lgMatch?.[1]?.trim();

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: 'Muestra extraída exitosamente'
    });

    if (lgCode) {
      changes.push({
        field: this.LBL_CODIGO_LG,
        oldValue: null,
        newValue: lgCode
      });
    }

    return changes;
  }

  private parseSequencingRunEditedMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: 'Corrida editada exitosamente'
    });

    // Try to extract JSON from message
    const jsonMatch = /\{.+\}/s.exec(message);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]) as Record<string, unknown>;

        const fieldMap: Record<string, string> = {
          runCode: this.LBL_CODIGO_CORRIDA,
          technology: this.LBL_TECNOLOGIA,
          status: this.LBL_ESTADO,
          equipment: 'Equipo',
          version: 'Versión',
          indexType: 'Tipo de Índice',
          cellIdentifier: 'Identificador Celda',
          cellLength: 'Longitud Celda',
          runName: 'Nombre Corrida',
          librariesCount: 'Librerías Asignadas'
        };

        for (const [key, value] of Object.entries(parsed)) {
          if (value !== null && value !== undefined && value !== '-' && value !== '') {
            const fieldName = fieldMap[key] || key;
            changes.push({
              field: fieldName,
              oldValue: null,
              newValue: String(value)
            });
          }
        }
      } catch {
        // If JSON parsing fails, try to extract RUN code from legacy format
        const runMatch = /RUN:\s*([A-Z0-9]+)/i.exec(message);
        if (runMatch) {
          changes.push({
            field: this.LBL_CODIGO_CORRIDA,
            oldValue: null,
            newValue: runMatch[1]
          });
        }
      }
    } else {
      // Legacy format without JSON
      const runMatch = /RUN:\s*([A-Z0-9]+)/i.exec(message);
      if (runMatch) {
        changes.push({
          field: this.LBL_CODIGO_CORRIDA,
          oldValue: null,
          newValue: runMatch[1]
        });
      }
    }

    return changes;
  }

  private parseReprocessInSuccessfulRunMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    // Extract the action type (e.g., "REPROCESO EN CORRIDA EXITOSA NGS")
    const actionMatch = /^(REPROCESO EN CORRIDA EXITOSA\s*[áéíóúñ\w\s]*)/i.exec(message);
    const actionLabel = actionMatch
      ? this.formatActionLabel(actionMatch[1].trim())
      : 'Reproceso en Corrida Exitosa';

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: actionLabel
    });

    // Try to extract JSON from message
    const jsonMatch = /\{.+\}/s.exec(message);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]) as Record<string, unknown>;

        const fieldMap: Record<string, string> = {
          lgCode: this.LBL_CODIGO_LG,
          runCode: this.LBL_CODIGO_CORRIDA,
          technology: this.LBL_TECNOLOGIA,
          cause: this.LBL_CAUSA,
          status: this.LBL_ESTADO,
          observations: this.LBL_OBSERVACION
        };

        for (const [key, value] of Object.entries(parsed)) {
          if (value !== null && value !== undefined && value !== '' && value !== '-') {
            const fieldName = fieldMap[key] || key;
            changes.push({
              field: fieldName,
              oldValue: null,
              newValue: String(value)
            });
          }
        }
      } catch {
        // If JSON parsing fails, just return the action
      }
    }

    return changes;
  }

  private parseReprocessInFailedRunMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    // Extract the action type (e.g., "REPROCESO EN CORRIDA NO EXITOSA NGS")
    const actionMatch = /^(REPROCESO EN CORRIDA NO EXITOSA\s*[áéíóúñ\w\s]*)/i.exec(message);
    const actionLabel = actionMatch
      ? this.formatActionLabel(actionMatch[1].trim())
      : 'Reproceso en Corrida No Exitosa';

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: actionLabel
    });

    // Try to extract JSON from message
    const jsonMatch = /\{.+\}/s.exec(message);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]) as Record<string, unknown>;

        const fieldMap: Record<string, string> = {
          lgCode: this.LBL_CODIGO_LG,
          runCode: this.LBL_CODIGO_CORRIDA,
          technology: this.LBL_TECNOLOGIA,
          cause: this.LBL_CAUSA,
          status: this.LBL_ESTADO,
          observations: this.LBL_OBSERVACION
        };

        for (const [key, value] of Object.entries(parsed)) {
          if (value !== null && value !== undefined && value !== '' && value !== '-') {
            const fieldName = fieldMap[key] || key;
            changes.push({
              field: fieldName,
              oldValue: null,
              newValue: String(value)
            });
          }
        }
      } catch {
        // If JSON parsing fails, just return the action
      }
    }

    return changes;
  }

  private parseDetailedMessage(message: string): VersionChange[] {
    const changes: VersionChange[] = [];

    changes.push({
      field: this.LBL_ACCION,
      oldValue: null,
      newValue: 'Guardado Definitivo - Enviado a Laboratorio'
    });

    const fieldLabels: Record<string, string> = {
      specimenCode: this.LBL_CODIGO_MUESTRA,
      lgCode: this.LBL_CODIGO_LG,
      patientId: 'ID Paciente',
      patientIdentification: 'Documento',
      patientIdentificationType: 'Tipo Documento',
      patientFirstName: 'Nombre',
      patientSecondName: 'Segundo Nombre',
      patientLastName: 'Apellido',
      patientSecondLastName: 'Segundo Apellido',
      birthDate: 'Fecha Nacimiento',
      administrativeSex: 'Sexo',
      orderId: 'Número Orden',
      examCode: 'Código Examen',
      specimenType: 'Tipo Muestra',
      regional: 'Regional',
      facility: 'Sede',
      labEntryDate: 'Fecha Ingreso Lab',
      priority: 'Prioridad',
      physicalLocation: 'Ubicación Física'
    };

    const dataRegex = /Enviado a Laboratorio\.\s*(.+)/s;
    const dataMatch = dataRegex.exec(message);
    const dataPart = dataMatch?.[1];
    if (dataPart) {
      const fieldRegex = /(\w+):\s*([^,]+?)(?:,\s*|$)/g;
      let match;

      while ((match = fieldRegex.exec(dataPart)) !== null) {
        const key = match[1].trim();
        const value = match[2].trim();

        if (value && value !== '') {
          const label = fieldLabels[key] || this.formatFieldName(key);
          changes.push({
            field: label,
            oldValue: null,
            newValue: value
          });
        }
      }
    }

    return changes;
  }

  private formatFieldName(field: string): string {
    // Convert camelCase or snake_case to readable format in lowercase
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim();
  }

  private buildQueryParams(params?: CasesQueryParams): string {
    if (!params) {
      return '?page=0&size=10';
    }

    const queryParts: string[] = [];

    queryParts.push(`page=${params.page ?? 0}`);
    queryParts.push(`size=${params.size ?? 10}`);

    this.appendSimpleParams(params, queryParts);

    if (params.filters) {
      this.appendFilterParams(params.filters, queryParts);
    }

    return `?${queryParts.join('&')}`;
  }

  private appendSimpleParams(params: CasesQueryParams, queryParts: string[]): void {
    const simpleFields: (keyof CasesQueryParams)[] = [
      'searchQuery', 'searchField', 'priority', 'status',
      'dateFrom', 'dateTo', 'sortBy', 'sortDirection'
    ];

    for (const field of simpleFields) {
      const value = params[field];
      if (value && typeof value === 'string') {
        queryParts.push(`${field}=${encodeURIComponent(value)}`);
      }
    }
  }

  private appendFilterParams(filters: NonNullable<CasesQueryParams['filters']>, queryParts: string[]): void {
    const codeFilters: { key: string; items: { code: string }[] | undefined }[] = [
      { key: 'examType', items: filters.examType },
      { key: 'status', items: filters.sampleStatus },
      { key: 'sampleType', items: filters.sampleType },
      { key: 'facility', items: filters.facility }
    ];

    for (const { key, items } of codeFilters) {
      if (items && items.length > 0) {
        const codes = items.map((i) => i.code).join(',');
        queryParts.push(`${key}=${encodeURIComponent(codes)}`);
      }
    }

    if (filters.priority && filters.priority.length > 0) {
      const priorities = filters.priority.join(',');
      queryParts.push(`priority=${encodeURIComponent(priorities)}`);
    }

    if (filters.startDate) {
      const dateStr = this.formatDateForBackend(filters.startDate);
      queryParts.push(`dateFrom=${encodeURIComponent(dateStr)}`);
    }
    if (filters.endDate) {
      const dateStr = this.formatDateForBackend(filters.endDate);
      queryParts.push(`dateTo=${encodeURIComponent(dateStr)}`);
    }
  }

  private formatDateForBackend(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  updatePrioritization(
    lgCode: string,
    request: UpdatePrioritizationRequest
  ): Observable<void> {
    const baseUrl = this.appConfig.backend.baseUrl || '';
    const url = `${baseUrl}${this.appConfig.backend.context}/specimens/${encodeURIComponent(lgCode)}/prioritization`;
    return this.http
      .put<void>(url, request)
      .pipe(
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          throw error;
        })
      );
  }
}
